import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useListNav, scrollActiveIntoView } from "@/lib/keys";

/**
 * ComboBox — pick from a managed list OR add a new value, with type-to-search.
 * Replaces free-text fields where we have a known set (category, subcategory,
 * manufacturer, vendor) but still want to allow new entries. Options are
 * `{value,label}` so it works for code-backed lists (vendors) too; pass
 * strings via `stringOpts`.
 *
 * KEYS-01 (Seth 2026-09-22): rebuilt keyboard-first. The box IS the text
 * field — Tab into it and start typing, the list filters as you go; ↓ ↑ move
 * the highlight, Enter picks, Esc puts the old value back, Tab picks the
 * highlighted match when you typed something and moves on to the next field.
 * The mouse still works exactly as before.
 */
export interface ComboOption { value: string; label: string; hint?: string }

export const stringOpts = (xs: string[]): ComboOption[] =>
  xs.map((x) => ({ value: x, label: x }));

export function ComboBox({
  value, onChange, options, placeholder = "Select…", allowAdd = true, className, autoFocus, id,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  options: ComboOption[];
  placeholder?: string;
  allowAdd?: boolean;
  className?: string;
  /** Marks the box as the field a window opens on (Modal reads data-autofocus). */
  autoFocus?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null); // null = showing the current value
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const current = options.find((o) => o.value === value);
  const display = current?.label ?? value ?? "";
  const text = query ?? display;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    const q = (query ?? "").trim().toLowerCase();
    const base = q
      ? options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
      : options;
    return base.slice(0, 60);
  }, [options, query]);

  const q = (query ?? "").trim();
  const exact = options.some((o) => o.label.toLowerCase() === q.toLowerCase());
  const canAdd = allowAdd && q.length > 0 && !exact;
  const rows = canAdd ? filtered.length + 1 : filtered.length; // the "Add" row is the last one

  const close = () => { setOpen(false); setQuery(null); };
  const pick = (v: string | null) => { onChange(v); close(); };
  const pickIndex = (i: number) => {
    if (i < filtered.length) pick(filtered[i].value);
    else if (canAdd) pick(q);
  };

  const nav = useListNav({
    count: rows,
    open,
    typed: q.length > 0,
    onPick: pickIndex,
    onClose: close,
  });
  useEffect(() => { if (open) scrollActiveIntoView(listRef.current, nav.active); }, [open, nav.active]);

  return (
    <div ref={wrapRef} className="relative">
      <div className={cn(
        "flex w-full items-center gap-1 rounded-md border border-hairline bg-white px-2.5 py-2 text-sm focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/25",
        className,
      )}>
        <input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={open ? `${id ?? "combo"}-list` : undefined}
          data-autofocus={autoFocus ? "" : undefined}
          value={text}
          placeholder={placeholder}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={(e) => { e.target.select(); }}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (!open && e.key === "ArrowDown") { e.preventDefault(); setOpen(true); return; }
            nav.onKeyDown(e);
          }}
          onBlur={() => { /* the document mousedown handler closes on outside clicks; a Tab-away closes here */ window.setTimeout(() => { if (wrapRef.current && !wrapRef.current.contains(document.activeElement)) close(); }, 0); }}
          className="min-w-0 flex-1 bg-transparent text-left outline-none placeholder:text-slate-400"
          autoComplete="off"
          spellCheck={false}
        />
        {value && (
          <button type="button" tabIndex={-1} aria-label="Clear" onMouseDown={(e) => { e.preventDefault(); pick(null); inputRef.current?.focus(); }} className="shrink-0 rounded p-0.5 text-slate-300 hover:text-slate-500">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <button type="button" tabIndex={-1} aria-label={open ? "Close the list" : "Open the list"} onMouseDown={(e) => { e.preventDefault(); if (open) close(); else { setOpen(true); inputRef.current?.focus(); } }} className="shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-600">
          <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
        </button>
      </div>

      {open && (
        <div ref={listRef} id={`${id ?? "combo"}-list`} role="listbox" data-glass="panel" className="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-xl py-1">
          {filtered.map((o, i) => (
            <button
              key={o.value} type="button" role="option" aria-selected={value === o.value} data-index={i} tabIndex={-1}
              onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
              onMouseEnter={() => nav.setActive(i)}
              className={cn("flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm", nav.active === i ? "bg-brand-50 text-brand-900" : "hover:bg-slate-50")}
            >
              <span className="truncate">
                {o.label}
                {o.hint && <span className="ml-1 text-[11px] text-slate-400">{o.hint}</span>}
              </span>
              {value === o.value && <Check className="h-3.5 w-3.5 shrink-0 text-brand-600" />}
            </button>
          ))}
          {filtered.length === 0 && !canAdd && (
            <p className="px-3 py-2 text-xs text-slate-400">No matches</p>
          )}
          {canAdd && (
            <button
              type="button" role="option" aria-selected={false} data-index={filtered.length} tabIndex={-1}
              onMouseDown={(e) => { e.preventDefault(); pick(q); }}
              onMouseEnter={() => nav.setActive(filtered.length)}
              className={cn("flex w-full items-center gap-1.5 border-t border-hairline px-3 py-1.5 text-left text-sm text-brand-700", nav.active === filtered.length ? "bg-brand-50" : "hover:bg-brand-50")}
            >
              <Plus className="h-3.5 w-3.5" /> Add “{q}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
